'use client';

import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import { useState } from 'react';

import { Image } from '@/components/client';

import { Rating } from '../Rating';

interface ReviewCardProps {
  rating: number;
  createdAt: string;
  productName: string;
  contents: string;
  reviewer: string;
  imageUrlList: string[];
  answer?: string;
  answerAt?: string;
  /** 추가 액션 요소 (드롭다운 메뉴 등), 오른쪽 상단에 렌더링 */
  actions?: React.ReactNode;
  /** 이미지 클릭 콜백 */
  onImageClick?: () => void;
}

export const ReviewCard = ({
  rating,
  createdAt,
  productName,
  contents = '',
  reviewer,
  imageUrlList = [],
  answer = '',
  answerAt,
  actions,
  onImageClick,
}: ReviewCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps',
  });

  return (
    <div className="relative border-t-1 border-Gray-200 p-20 text-left first:border-t-0">
      {actions ? <div className="absolute right-20 top-20">{actions}</div> : null}
      <Rating value={rating} size={16} className="mb-8" />
      <div className="mb-6 flex items-center gap-8">
        <span className="text-Gray-550 typo-caption1">{reviewer}</span>
        <div className="h-10 border-l-1 border-Gray-300" />
        <span className="text-Gray-550 typo-caption1">
          {dayjs(createdAt).format('YY.MM.DD')}
        </span>
      </div>
      <p className="mb-16 font-400 text-Gray-600 typo-body4">{productName}</p>
      {imageUrlList.length > 0 && (
        <div className="mb-16">
          <div className="relative w-full overflow-hidden" ref={emblaRef}>
            <div className="flex gap-8">
              {imageUrlList.map((url, index) => (
                <div
                  key={index}
                  className={cn('w-auto flex-[0_0_auto]', onImageClick && 'cursor-pointer')}
                  onClick={onImageClick}
                >
                  <Image
                    src={url}
                    alt={`${productName}-${index + 1}`}
                    width={128}
                    height={128}
                    className="block aspect-square rounded-large object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {contents.length > 100 ? (
        <p className="whitespace-pre-line font-500 text-Gray-800 typo-body3">
          {/* 더보기 상태에 따라 전체 내용 또는 100자까지만 표시 */}
          {isExpanded ? contents : `${contents.slice(0, 100)}...`}
          {!isExpanded && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(true);
                }
              }}
              className="inline-block cursor-pointer whitespace-nowrap px-4 font-600 text-Gray-550 typo-body3"
            >
              더보기
            </span>
          )}
        </p>
      ) : (
        <p className="whitespace-pre-line font-500 text-Gray-800 typo-body3">
          {contents}
        </p>
      )}
      {answer ? (
        <div className="mt-16 flex w-full flex-col rounded-medium bg-Gray-25 p-20">
          <div className="flex items-center justify-between pb-4">
            <p className="font-600 text-Gray-700 typo-body4">판매자</p>
            {answerAt ? (
              <p className="font-400 text-Gray-550 typo-caption1">
                {dayjs(answerAt).format('YY.MM.DD')}
              </p>
            ) : null}
          </div>
          <p className="whitespace-pre-line pb-8 font-400 text-Gray-700 typo-body4">
            {answer.length > AnswerExpandedPoint ? (
              <>
                {isAnswerExpanded
                  ? answer
                  : `${answer.slice(0, AnswerExpandedPoint)}...`}
              </>
            ) : (
              answer
            )}
          </p>
          {answer.length > AnswerExpandedPoint && (
            <Button className="mt-8 size-fit p-0" theme="sub-text" asChild>
              <div
                className="flex cursor-pointer items-center gap-4"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAnswerExpanded((prev) => !prev);
                }}
              >
                <p className="font-600 text-Gray-550 typo-caption1">
                  {isAnswerExpanded ? '접기' : '더보기'}
                </p>
                <SvgChevrondown
                  className={cn(
                    'size-12 text-Gray-550 transition-transform duration-300',
                    {
                      '-rotate-180': isAnswerExpanded,
                    },
                  )}
                />
              </div>
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};

const AnswerExpandedPoint = 60;
